import type { StatusPosicao } from '@wms/shared';

/**
 * Paleta Okabe-Ito (doc 06) — segura para protanopia, deuteranopia e
 * tritanopia. Regra dura: cor NUNCA é o único canal. Cada status carrega
 * também um glifo e um padrão de desenho no mapa.
 */
export interface EstiloStatus {
  rotulo: string;
  /** Glifo curto, usado na célula do mapa e no badge. */
  glifo: string;
  /** Como o mapa desenha a célula, além da cor. */
  padrao: 'solido' | 'contorno' | 'tracejado' | 'cruzado';
  classeBadge: string;
  /** Cor de traço/preenchimento no canvas, tema claro. */
  hexClaro: string;
  hexClaroFundo: string;
  hexEscuro: string;
  hexEscuroFundo: string;
  descricao: string;
}

export const ESTILO_STATUS: Record<StatusPosicao, EstiloStatus> = {
  livre: {
    rotulo: 'Livre',
    glifo: '▢',
    padrao: 'contorno',
    classeBadge: 'bg-status-livreFundo text-status-livre border-status-livre/40',
    hexClaro: '#009E73',
    hexClaroFundo: '#E6F7F1',
    hexEscuro: '#29BF91',
    hexEscuroFundo: '#0D2D25',
    descricao: 'Sem saldo. Disponível para armazenagem.',
  },
  ocupada: {
    rotulo: 'Ocupada',
    glifo: '▣',
    padrao: 'solido',
    classeBadge: 'bg-status-ocupadaFundo text-status-ocupada border-status-ocupada/40',
    hexClaro: '#0072B2',
    hexClaroFundo: '#EAF6FD',
    hexEscuro: '#38A0D8',
    hexEscuroFundo: '#102C3F',
    descricao: 'Com saldo positivo.',
  },
  divergencia: {
    rotulo: 'Divergência',
    glifo: '⚠',
    padrao: 'tracejado',
    classeBadge: 'bg-status-divergenciaFundo text-status-divergencia border-status-divergencia/40',
    hexClaro: '#D55E00',
    hexClaroFundo: '#FBE9E2',
    hexEscuro: '#F07C2A',
    hexEscuroFundo: '#3E1E0C',
    descricao: 'Saldo negativo — estoque saiu sem baixa correspondente.',
  },
  indisponivel: {
    rotulo: 'Indisponível',
    glifo: '✕',
    padrao: 'cruzado',
    classeBadge: 'bg-status-indisponivelFundo text-status-indisponivel border-status-indisponivel/40',
    hexClaro: '#6B7280',
    hexClaroFundo: '#F1F2F4',
    hexEscuro: '#8E94A0',
    hexEscuroFundo: '#262930',
    descricao: 'Posição inativa no cadastro.',
  },

  // --------------------------------------------------------------------
  // Os dois abaixo existem no tipo compartilhado mas a API NUNCA os produz:
  // não há capacidade cadastrada em CADASTROS_LOCALIZADORES (parcial) nem
  // status operacional de bloqueio (bloqueada). Ficam aqui só para o switch
  // continuar exaustivo — e estão FORA de STATUS_VISIVEIS, então não aparecem
  // em legenda, filtro ou contagem. Mostrar um estado que não ocorre é pior
  // que não mostrá-lo: ensina o operador a procurar o que não existe.
  // --------------------------------------------------------------------
  parcial: {
    rotulo: 'Parcial',
    glifo: '▤',
    padrao: 'contorno',
    classeBadge: 'bg-superficie2 text-textoFraco border-borda',
    hexClaro: '#56B4E9',
    hexClaroFundo: '#EAF6FD',
    hexEscuro: '#56B4E9',
    hexEscuroFundo: '#102C3F',
    descricao: 'Não calculável hoje: falta capacidade cadastrada.',
  },
  bloqueada: {
    rotulo: 'Bloqueada',
    glifo: '⊘',
    padrao: 'cruzado',
    classeBadge: 'bg-superficie2 text-textoFraco border-borda',
    hexClaro: '#E69F00',
    hexClaroFundo: '#FFF3E0',
    hexEscuro: '#F0B22C',
    hexEscuroFundo: '#3A2A08',
    descricao: 'Não existe status de bloqueio no banco hoje.',
  },
};

/** A única lista que a UI deve iterar: os status que a API realmente produz. */
export const STATUS_VISIVEIS: readonly StatusPosicao[] = [
  'ocupada',
  'livre',
  'divergencia',
  'indisponivel',
] as const;

export function ehStatusVisivel(valor: string): valor is StatusPosicao {
  return (STATUS_VISIVEIS as readonly string[]).includes(valor);
}

export interface CoresMapa {
  fundo: string;
  grade: string;
  texto: string;
  textoFraco: string;
  realce: string;
  porStatus: Record<StatusPosicao, { traco: string; preenchimento: string }>;
}

/**
 * O canvas não herda CSS, então o tema precisa ser resolvido para hex aqui.
 * Recalculado quando o usuário troca de tema (ver useTema).
 */
export function coresDoMapa(escuro: boolean): CoresMapa {
  const entradas = Object.entries(ESTILO_STATUS) as [StatusPosicao, EstiloStatus][];
  const porStatus = {} as CoresMapa['porStatus'];
  for (const [chave, estilo] of entradas) {
    porStatus[chave] = {
      traco: escuro ? estilo.hexEscuro : estilo.hexClaro,
      preenchimento: escuro ? estilo.hexEscuroFundo : estilo.hexClaroFundo,
    };
  }
  return {
    fundo: escuro ? '#101217' : '#FAFAF9',
    grade: escuro ? '#2D323B' : '#E0E0E4',
    texto: escuro ? '#EDEFF3' : '#18181B',
    textoFraco: escuro ? '#9AA0AC' : '#6A6A74',
    realce: escuro ? '#F0B22C' : '#E69F00',
    porStatus,
  };
}

/** Escala de calor para ocupação de corredor (zoom afastado). Monocromática
 *  azul → não colide com o vermelhão de divergência em daltonismo. */
export function corDeCalor(percentual: number, escuro: boolean): string {
  const p = Math.max(0, Math.min(100, percentual)) / 100;
  const claros = ['#F2F6F9', '#D5E7F4', '#A9CFE8', '#6FAFD6', '#2E85BE', '#0072B2'];
  const escuros = ['#1A1E25', '#1D3244', '#204A66', '#256687', '#2A85AC', '#38A0D8'];
  const escala = escuro ? escuros : claros;
  const indice = Math.min(escala.length - 1, Math.floor(p * escala.length));
  return escala[indice] ?? escala[0] ?? '#000000';
}
